import i18next from 'i18next';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getSocket } from '../../services/socket';
import { RootState } from '../../store';
import {
	buildWhiskeyTableLabel,
	extractGameBroadcastPayload,
	fillTranslationPlaceholders,
	formatChipsForBroadcast,
} from '../../utils/helpers/globalBroadcastMessage';
import { showGameBroadcastToast } from './showGameBroadcastToast';

/**
 * Listens for site-wide game broadcasts (jackpot, Vegas big win, gold tournament, whiskey)
 * on the game socket — same events the legacy Angular client used — so all logged-in clients see them.
 *
 * Also handles `showPlayerKicked`, which is addressed to one player rather than
 * broadcast, but belongs here for the same reason v1 put it in init.service.ts: it
 * has to land whether or not the player currently has the table screen mounted.
 */
const GlobalGameBroadcastListener = () => {
	const isLoggedIn = useSelector((s: RootState) => !!s.auth?.user);
	const playerId = useSelector((s: RootState) => s.auth?.user?.id || '');
	const username = useSelector((s: RootState) => s.auth?.user?.username || '');

	useEffect(() => {
		if (!isLoggedIn || !playerId || !username) {
			return;
		}

		const socket = getSocket(String(playerId), String(username));

		const onJackpotWon = (raw: unknown) => {
			const p = extractGameBroadcastPayload(raw);
			if (!p?.username) {
				return;
			}
			const template = i18next.t('T_SITE_NOTIFICATION_JACKPOT', {
				defaultValue: '%% won %% from Jackpot %%',
			});
			const message = fillTranslationPlaceholders(template, [
				p.username,
				formatChipsForBroadcast(p.value),
				String(p.type ?? ''),
			]);
			showGameBroadcastToast('broadcastJackpot', message, {
				jackpotType: Number(p.type) || 1,
			});
		};

		const onVegasBigWin = (raw: unknown) => {
			const p = extractGameBroadcastPayload(raw);
			if (!p?.username) {
				return;
			}
			const template = i18next.t('T_SITE_NOTIFICATION_VEGAS_WIN', {
				defaultValue: '%% won %% from Las Vegas #%%',
			});
			const message = fillTranslationPlaceholders(template, [
				p.username,
				formatChipsForBroadcast(p.value),
				String(p.tableName ?? ''),
			]);
			showGameBroadcastToast('broadcastVegas', message);
		};

		const onTournamentWin = (raw: unknown) => {
			const p = extractGameBroadcastPayload(raw);
			if (!p?.username) {
				return;
			}
			const template = i18next.t('T_SITE_NOTIFICATION_TOURNAMENT', {
				defaultValue: '%% won %% from Tournament %%',
			});
			const message = fillTranslationPlaceholders(template, [
				p.username,
				formatChipsForBroadcast(p.value),
				String(p.tableName ?? ''),
			]);
			showGameBroadcastToast('broadcastTournament', message);
		};

		const onWhiskeySent = (raw: unknown) => {
			const p = extractGameBroadcastPayload(raw);
			if (!p?.username) {
				if (process.env.NODE_ENV !== 'production') {
					console.warn('[GameBroadcast] whiskeySent ignored — bad payload', raw);
				}
				return;
			}
			const template = i18next.t('T_SITE_NOTIFICATION_WHISKEY', {
				defaultValue: '%% sent whiskey to %%',
			});
			const tableLabel = buildWhiskeyTableLabel(p.tableName, p.roomName);
			const place = tableLabel || i18next.t('T_SITE_LOBBY_ROOM_TABLE', { defaultValue: 'table' });
			const message = fillTranslationPlaceholders(template, [p.username, place]);
			showGameBroadcastToast('broadcastWhiskey', message);
		};

		/**
		 * The player was removed from a table by its owner, or dropped for
		 * inactivity. v1 handled this in init.service.ts: play the error sound and
		 * open a modal naming the table, choosing the message by the `inactive` flag.
		 *
		 * The server sends the table's *name* as `id` (v1's `table.name`, e.g.
		 * "Sofia 3"), which is what gets substituted into the message.
		 */
		const onPlayerKicked = (raw: unknown) => {
			// Not extractGameBroadcastPayload: that one requires a `username`, which the
			// broadcasts have and this payload does not. Shape is
			// `{ method, recipient, data: { id, inactive? } }`.
			const envelope = (raw ?? {}) as { data?: { id?: unknown; inactive?: unknown } };
			const p = envelope.data ?? (raw as { id?: unknown; inactive?: unknown });

			const tableLabel = p?.id == null ? '' : String(p.id);
			const wasInactive = p?.inactive === true;

			const template = wasInactive
				? i18next.t('T_SITE_MAIN_KICKED_INACTIVE', {
						defaultValue: 'You were removed from table #%% due to inactivity.',
					})
				: i18next.t('T_SITE_MAIN_KICKED', {
						defaultValue: 'You have been removed from table %%',
					});

			showGameBroadcastToast('error', fillTranslationPlaceholders(template, [tableLabel]));
		};

		socket.on('jackpotWon', onJackpotWon);
		socket.on('showPlayerKicked', onPlayerKicked);
		socket.on('vegasBigWin', onVegasBigWin);
		socket.on('tournamentWin', onTournamentWin);
		socket.on('whiskeySent', onWhiskeySent);

		return () => {
			socket.off('jackpotWon', onJackpotWon);
			socket.off('showPlayerKicked', onPlayerKicked);
			socket.off('vegasBigWin', onVegasBigWin);
			socket.off('tournamentWin', onTournamentWin);
			socket.off('whiskeySent', onWhiskeySent);
		};
	}, [isLoggedIn, playerId, username]);

	return null;
};

export default GlobalGameBroadcastListener;
